"use client"

import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card"
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table"
import { AcknowledgeButton } from "@/app/(app)/(authed)/dashboard/alerts/acknowledge-button"

interface Alert {
  id: string
  siteId: string
  type: string
  severity: string
  message: string
  engineId: string | null
  acknowledged: boolean
  createdAt: Date
}

interface AlertsViewProps {
  alerts: Alert[]
  siteNameMap: Map<string, string>
  readOnly?: boolean
}

const severityColor: Record<string, string> = {
  high: "bg-red-600 hover:bg-red-600 text-white",
  medium: "bg-yellow-600 hover:bg-yellow-600 text-white",
  low: "bg-blue-600 hover:bg-blue-600 text-white",
}

const TYPE_LABELS: Record<string, string> = {
  after_hours: "After Hours",
  fuel: "Fuel",
  anomaly: "Anomaly",
}

export function AlertsView({ alerts, siteNameMap, readOnly }: AlertsViewProps) {
  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-2xl font-bold">{"Alerts"}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{"Alerts across all your monitored sites"}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{`All Alerts (${alerts.length})`}</CardTitle>
          <CardDescription>{"Sorted by most recent first"}</CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-lg font-medium text-muted-foreground">{"No alerts. Your engines are running normally."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{"Site"}</TableHead>
                  <TableHead>{"Type"}</TableHead>
                  <TableHead>{"Severity"}</TableHead>
                  <TableHead>{"Message"}</TableHead>
                  <TableHead>{"Engine"}</TableHead>
                  <TableHead>{"Time"}</TableHead>
                  {!readOnly && <TableHead className="text-center">{"Ack"}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow
                    key={alert.id}
                    className={alert.acknowledged ? "opacity-50" : ""}
                  >
                    <TableCell className="font-medium">
                      {siteNameMap.get(alert.siteId) || alert.siteId}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {TYPE_LABELS[alert.type] ?? alert.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={severityColor[alert.severity] || ""}>
                        {alert.severity.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">
                      {alert.message}
                    </TableCell>
                    <TableCell className="text-sm">
                      {alert.engineId || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {alert.createdAt.toLocaleString()}
                    </TableCell>
                    {!readOnly && (
                      <TableCell className="text-center">
                        <AcknowledgeButton
                          alertId={alert.id}
                          acknowledged={alert.acknowledged}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
